import axios from 'axios';
import { Subject } from 'rxjs';
import { v4 as uuid } from 'uuid';
import { IDatabase, MemoryDatabase } from '../database';
import {
  IFlowyNotification,
  IFlowyProject,
  IGizmoEvent,
  IGlobalConfigs,
  IGlobalFeatures,
  IGlobalSync,
  ILocationData,
  IPlatformInterface,
  IRealmGlobal,
  ISingleFeature,
} from '../types';
import { FlowyAnalytics } from '../analytics';
import FlowyFoundation from './flowy-foundation';
import { Dao } from '../dao';
import {
  updateSyncObject,
  setCache,
  getCache,
  deleteCache,
  setFeatures,
} from './global-functions';
import { defaultCoreProject } from '../utils/fixtures';
import { downloadProjects } from '../flowy/global-functions/update-sync-object';

interface IConstructor {
  apiUrl: string;
  platformInterface?: IPlatformInterface;
  database?: IDatabase;
}

/**
 * A single instance of this class will be located on the outer most provider. The objective of
 * this is to host class instances that should be singletos. Like the database and the platform
 * interface.
 */
class FlowyGlobal {
  /**
   * Url to the location of the api
   */
  private _apiUrl: string;
  /**
   * Global configurations
   */
  private _config: IGlobalConfigs;
  /**
   * Global sync object
   */
  private _sync: IGlobalSync;
  /**
   * Class with the methods used to interact with the platform layer
   */
  private _platformInterface?: IPlatformInterface;
  /**
   * Database instance, depending on the platform it will be initialized
   */
  private _database?: IDatabase;
  /**
   * Flowy analytics instance
   */
  private _analytics?: FlowyAnalytics;
  private _dao?: Dao;
  /**
   * Global features object
   */
  private _features: IGlobalFeatures;
  /**
   * Global cache object
   */
  private _cache: any;

  /**
   * Notifications stream which will be used by the platform layer to display the
   * appropiate controls
   */
  public notifications: Subject<IFlowyNotification>;
  /**
   * Global events stream which will be used by the core to communicate between RootGizmos,
   * right now it's basically used to communicated 'annex_form'
   */
  public globalEvents: Subject<IGizmoEvent>;
  public globalConfig: Subject<IGlobalConfigs>;
  public globalSync: Subject<IGlobalSync>;
  public features: Subject<IGlobalFeatures>;

  // TODO: when we have database support we're going to add the Dao
  // private _dao?: Dao;
  private _foundations: { [key: string]: FlowyFoundation };

  constructor({ apiUrl, platformInterface, database }: IConstructor) {
    this._apiUrl = apiUrl;
    this._platformInterface = platformInterface;
    this._foundations = {};

    this.notifications = new Subject<IFlowyNotification>();
    this.globalEvents = new Subject<IGizmoEvent>();
    this.globalConfig = new Subject<IGlobalConfigs>();
    this.globalSync = new Subject<IGlobalSync>();
    this.features = new Subject<IGlobalFeatures>();

    this._config = {
      autoSync: true,
      offline: false,
      onlyWifi: false,
      trackLocation: false,
    };
    this._sync = {};
    this._features = {};
    this._cache = {};

    if (database) {
      this._database = database;
    }
  }

  public init = (): Promise<FlowyGlobal> => {
    return new Promise<FlowyGlobal>(async resolve => {
      const platformInterface = this.getPlatformInterface();
      axios.interceptors.response.use(
        function(response) {
          return response;
        },
        function(error) {
          if (
            error.response?.status === 401 &&
            error.response?.data?.detail === 'No auth'
          ) {
            platformInterface?.logout('session expired').then();
          }

          return Promise.reject(error);
        }
      );

      // TODO: bring configurations from the database, if they exist...

      // Right now by default we're going to use the memory database
      const user = await this._platformInterface?.getUser();
      if (!this._database) {
        // If no database is provided by the platfom layer we're going to use the memory database
        this._database = new MemoryDatabase({ user });
      }
      await this._database.init();

      try {
        // We're going to try to load the global configurations from the database
        const realmGlobal = await this._database.get({
          type: 'globals',
          fid: 'globalConfigs',
        });

        if (realmGlobal) {
          this._config = realmGlobal.data;
        }
      } catch (e) {}

      this._analytics = new FlowyAnalytics({
        flowyGlobal: this,
      });
      this._analytics.init();

      this._dao = new Dao({
        apiUrl: this._apiUrl,
        user,
        database: this._database,
        global: this,
      });

      // if (this._database.getType() !== 'memory') {
      //   await this.sync();
      // }

      resolve(this);
    });
  };

  /**
   * Register a FlowyFoundation instance
   *
   * @param {string} fid RootGizmoData fid
   * @param {FlowyFoundation} foundation FlowyFoundation instance
   */
  public registerFoundation = ({
    fid,
    foundation,
  }: IRegisterFoundationParams): void => {
    this._foundations[fid] = foundation;
  };

  /**
   * Get a registered FlowyFoundation instance
   * @param {string} fid RootGizmoData fid
   * @returns
   */
  public getFoundation = ({
    fid,
  }: IGetFoundationParams): FlowyFoundation | undefined => {
    return this._foundations[fid];
  };

  /**
   * Remove a registered FlowyFoundation instance
   *
   * @param {string} fid RootGizmoData fid
   */
  public unregisterFoundation = ({
    fid,
  }: IUnregisterFoundationParams): void => {
    delete this._foundations[fid];
  };

  /**
   * Get the apiUrl
   * @returns {string} _apiUrl
   */
  public getApiUrl = (): string => {
    return this._apiUrl;
  };

  /**
   * Set value int FlowyGlobal features object
   *
   * @param {ISingleFeature} singleFeatures list of paths and values to set
   */
  public setFeatures = ({ singleFeatures }: ISetFeaturesParams): void => {
    setFeatures({
      singleFeatures,
      context: {
        features: this._features,
        featuresSubject: this.features,
      },
    });
  };

  /**
   * Get global features object
   * @returns {IGlobalFeatures} _features
   */
  public getFeatures = (): IGlobalFeatures => {
    return this._features;
  };

  /**
   * Set a single configuration field
   *
   * @param field configuration field
   * @param value configuration value
   */
  public setSingleConfig = async ({
    field,
    value,
  }: ISetSingleConfigParams): Promise<void> => {
    this._config[field] = value;

    // We're going to save the new conigurations on the database
    const realmGlobal: IRealmGlobal = {
      fid: 'globalConfigs',
      data: this._config,
    };
    await this._database?.set({
      type: 'globals',
      value: realmGlobal,
      update: true,
    });

    this.globalConfig.next(this._config);
  };

  /**
   * Global configuration object
   *
   * @returns {IGlobalConfigs} _config
   */
  public getConfig = (): IGlobalConfigs => {
    return this._config;
  };

  /**
   * Global sync object
   *
   * @returns {IGlobalSync} _sync
   */
  public getSync = (): IGlobalSync => {
    return this._sync;
  };

  /**
   * Set the global sync object and emit it to the globalSync stream
   *
   * @param {IGlobalSync} sync global sync object
   */
  public setSync = (sync: IGlobalSync): void => {
    this._sync = sync;

    this.globalSync.next(sync);
  };

  /**
   * Get the database
   * @returns {IDatabase} the global instance of the database
   */
  public getDatabase = (): IDatabase | undefined => {
    return this._database;
  };

  /**
   * Get the global platformInterface instance provided by the platform layer
   * @returns {IPlatformInterface} the global instnace of the platform interface
   */
  public getPlatformInterface = (): IPlatformInterface | undefined => {
    return this._platformInterface;
  };

  /**
   * Get the global analytics instance
   * @returns {FlowyAnalytics} the global instance of the analytics
   */
  public getAnalytics = (): FlowyAnalytics | undefined => {
    return this._analytics;
  };

  /**
   * Get projects from the server
   *
   * @param {boolean} authenticated if the user is authenticated or not
   * @returns
   */
  public getProjects = ({
    authenticated,
  }: IGetProjectsParams): Promise<IFlowyProject[]> => {
    return new Promise<IFlowyProject[]>(async resolve => {
      if (!authenticated) {
        return resolve([defaultCoreProject]);
      } else {
        const database = this.getDatabase();

        if (!database) {
          return resolve([defaultCoreProject]);
        }

        await downloadProjects({
          global: this,
        });

        try {
          const projects = await database.get({
            type: 'globals',
            fid: 'projects',
          });

          return resolve(projects.data);
        } catch (e) {
          console.log('FlowyGlobal.getProjects.error: ', e);
          return resolve([defaultCoreProject]);
        }
      }

      resolve([]);
    });
  };

  public setCache = ({ key, group, value }: ISetCache): void => {
    setCache({
      key,
      group,
      value,
      context: {
        cache: this._cache,
      },
    });
  };

  public getCache = ({ key, group }: IGetCache): any => {
    return getCache({
      key,
      group,
      context: {
        cache: this._cache,
      },
    });
  };

  public deleteCache = ({ key, group }: IDeleteCache): void => {
    deleteCache({
      key,
      group,
      context: {
        cache: this._cache,
      },
    });
  };

  /**
   * Save location data to the database
   *
   * @param {ILocationData} location location data
   */
  public saveLocation = async ({
    location,
  }: ISaveLocationParams): Promise<void> => {
    if (location.fid === undefined) {
      location.fid = uuid();
    }
    if (location.epoch === undefined) {
      location.epoch = Date.now();
    }
    this._database?.set({
      type: 'locations',
      value: location,
    });
  };

  public sync = async (): Promise<void> => {
    await this._dao?.sync();
  };

  /**
   * This function is called inside the dao.sync function, it's used to update the sync object.
   * Which is listened by the UI layer in order to display the appropiate controls.
   */
  public updateSyncObject = async ({
    type,
  }: IUpdateSyncObjectParams): Promise<void> => {
    await updateSyncObject({
      type,
      context: {
        global: this,
      },
    });
  };

  /**
   * Send a notification to the platform layer
   * @param {IFlowyNotification} notification notification data
   * @returns
   */
  public sendNotification = ({
    notification,
  }: ISendNotificationParams): Promise<void> => {
    return new Promise<void>(async resolve => {
      this.notifications.next(notification);

      resolve();
    });
  };

  /**
   * Send an event to all active FlowyFoundations which will then send it to the RootGizmo (in
   * case it's the destiny)
   * @param {IGizmoEvent} event Gizmo event that will be send to all active FlowyFoundations
   * @returns
   */
  public sendEvent = ({ event }: ISendEventParams): Promise<void> => {
    return new Promise<void>(async resolve => {
      this.globalEvents.next(event);
      resolve();
    });
  };
}

interface ISendNotificationParams {
  notification: IFlowyNotification;
}

interface ISendEventParams {
  event: IGizmoEvent;
}

interface IRegisterFoundationParams {
  fid: string;
  foundation: FlowyFoundation;
}

interface IGetFoundationParams {
  fid: string;
}

interface IUnregisterFoundationParams {
  fid: string;
}

interface ISaveLocationParams {
  location: ILocationData;
}

interface ISetSingleConfigParams {
  field: 'offline' | 'onlyWifi' | 'trackLocation' | 'autoSync';
  value: boolean;
}

interface IUpdateSyncObjectParams {
  type: 'start' | 'forms' | 'filledForms' | 'locations' | 'finish';
}

interface IGetProjectsParams {
  authenticated: boolean;
}

interface ISetCache {
  key: string;
  group?: string;
  value: any;
}

interface IGetCache {
  key: string;
  group?: string;
}

interface IDeleteCache {
  key?: string;
  group?: string;
}

interface ISetFeaturesParams {
  singleFeatures: ISingleFeature[];
}

export default FlowyGlobal;
